import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import Navbar from '../../../../ReusableBlocks/Navbar';
import PartnerOnBehalfSelection from '../Components/forms/PartnerOnBehalfSelection';
import ProjectForm from '../Components/forms/ProjectForm';
import {
  PARTNER_PORTAL_MY_PROJECTS,
  PARTNER_PORTAL_NOT_AUTHORIZED,
  PARTNER_PORTAL_PAE_QUEUE,
  PARTNER_PORTAL_SUBMIT,
} from '../Constants/partnerRoutes';
import { usePartnerCompanies } from '../Hooks/usePartnerCompanies';
import { usePartnerFormOptions } from '../Hooks/usePartnerFormOptions';
import { usePartnerProjects } from '../Hooks/usePartnerProjects';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import PartnerLayout from '../Layout/PartnerLayout';
import PageHeader from '../Components/shared/PageHeader';
import {
  getFirstFieldErrorMessage,
  getPartnerApiErrorMessage,
  PartnerApiError,
  scrollToFirstPartnerFormError,
  showPartnerProjectSubmittedPopup,
  showPartnerValidationErrorPopup,
  splitSubmitFieldErrors,
} from '../Utils/partnerApiFeedback';
import {
  EMPTY_PARTNER_ON_BEHALF_SELECTION,
  validatePartnerOnBehalfSelection,
} from '../Utils/partnerOnBehalfUtils';
import {
  clearSubmitProjectDraft,
  formHasTypedContent,
  getSubmitDraftUserKey,
  readSubmitProjectDraft,
  writeSubmitProjectDraft,
} from '../Utils/partnerSubmitDraftUtils';
import { getPartnerPrefillFromUser } from '../Utils/partnerUserUtils';

const UNSAVED_LEAVE_MESSAGE = 'You have unsaved project details. Leave this page and discard them?';

const confirmDiscardUnsavedChanges = async () => {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Unsaved changes',
    text: 'You have typed project details that are not submitted yet. Leave this page and discard them?',
    showCancelButton: true,
    confirmButtonText: 'Leave page',
    cancelButtonText: 'Stay',
    confirmButtonColor: '#b42318',
    cancelButtonColor: '#052140',
    reverseButtons: true,
    customClass: {
      title: 'swal-title-custom',
      text: 'swal-text-custom',
    },
  });
  return result.isConfirmed;
};

const SubmitProjectPage = () => {
  const access = usePartnerRoleAccess();
  const { createProject } = usePartnerProjects();
  const { formOptions, loading: optionsLoading } = usePartnerFormOptions();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth?.currentUser);
  const draftUserKey = useMemo(() => getSubmitDraftUserKey(currentUser), [currentUser]);
  const savedDraft = useMemo(() => readSubmitProjectDraft(draftUserKey), [draftUserKey]);

  const isPartnerSubmitMode = access.canSubmitAsPartner;
  const isStaffSubmitMode = access.canSubmitOnBehalf;

  const [submitOnBehalfEnabled, setSubmitOnBehalfEnabled] = useState(
    () => Boolean(isStaffSubmitMode && savedDraft?.submitOnBehalfEnabled),
  );
  const showOnBehalfSelection = isStaffSubmitMode && submitOnBehalfEnabled;

  const {
    companies,
    loading: companiesLoading,
    error: companiesError,
  } = usePartnerCompanies(showOnBehalfSelection);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [serverErrors, setServerErrors] = useState({});
  const [onBehalfSelection, setOnBehalfSelection] = useState(
    () => savedDraft?.onBehalfSelection || EMPTY_PARTNER_ON_BEHALF_SELECTION,
  );
  const [onBehalfErrors, setOnBehalfErrors] = useState({});
  const [formValues, setFormValues] = useState(() => savedDraft?.form || null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(() => {
    if (!savedDraft?.form) {
      return false;
    }
    return formHasTypedContent(savedDraft.form, {
      excludeFields: ['partnerName', 'csn'],
    });
  });
  const [formReady, setFormReady] = useState(() => !optionsLoading);
  const allowNavigationRef = useRef(false);
  const formValuesRef = useRef(formValues);
  const onBehalfSelectionRef = useRef(onBehalfSelection);
  const submitOnBehalfEnabledRef = useRef(submitOnBehalfEnabled);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    onBehalfSelectionRef.current = onBehalfSelection;
  }, [onBehalfSelection]);

  useEffect(() => {
    submitOnBehalfEnabledRef.current = submitOnBehalfEnabled;
  }, [submitOnBehalfEnabled]);

  useEffect(() => {
    if (!optionsLoading) {
      setFormReady(true);
    }
  }, [optionsLoading]);

  const persistDraft = useCallback((nextForm, nextOnBehalfEnabled, nextOnBehalfSelection) => {
    writeSubmitProjectDraft(draftUserKey, {
      form: nextForm,
      submitOnBehalfEnabled: nextOnBehalfEnabled,
      onBehalfSelection: nextOnBehalfSelection,
    });
  }, [draftUserKey]);

  const handleFormChange = useCallback((nextForm, isDirty) => {
    setFormValues(nextForm);
    setHasUnsavedChanges(Boolean(isDirty));
    if (isDirty) {
      persistDraft(nextForm, submitOnBehalfEnabledRef.current, onBehalfSelectionRef.current);
      return;
    }
    clearSubmitProjectDraft(draftUserKey);
  }, [draftUserKey, persistDraft]);

  const handleOnBehalfChange = useCallback((updates) => {
    setOnBehalfSelection((current) => {
      const next = { ...current, ...updates };
      if (formValuesRef.current) {
        persistDraft(formValuesRef.current, submitOnBehalfEnabledRef.current, next);
      }
      return next;
    });
    setOnBehalfErrors((current) => {
      const nextErrors = { ...current };
      Object.keys(updates).forEach((field) => {
        delete nextErrors[field];
        if (field === 'partnerCompanyId') {
          delete nextErrors.companyName;
        }
      });
      return nextErrors;
    });
    setSubmitError('');
  }, [persistDraft]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      if (allowNavigationRef.current) {
        return undefined;
      }
      event.preventDefault();
      event.returnValue = UNSAVED_LEAVE_MESSAGE;
      return UNSAVED_LEAVE_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const handleDocumentClick = async (event) => {
      if (allowNavigationRef.current || submitting) {
        return;
      }

      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      let nextPath = href;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) {
          return;
        }
        nextPath = `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return;
      }

      if (nextPath === PARTNER_PORTAL_SUBMIT || nextPath.startsWith(`${PARTNER_PORTAL_SUBMIT}?`)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const shouldLeave = await confirmDiscardUnsavedChanges();
      if (!shouldLeave) {
        return;
      }

      allowNavigationRef.current = true;
      clearSubmitProjectDraft(draftUserKey);
      setHasUnsavedChanges(false);
      navigate(nextPath);
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [draftUserKey, hasUnsavedChanges, navigate, submitting]);

  if (!access.canAccessSubmitProject) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  if (!isPartnerSubmitMode && !isStaffSubmitMode) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  const { partnerName, partnerEmail, csn } = getPartnerPrefillFromUser(currentUser);
  const partnerTier = currentUser?.tier || currentUser?.partnerTier || 'Premier';
  const accountBadgeText = (isPartnerSubmitMode || (isStaffSubmitMode && !submitOnBehalfEnabled))
    && (partnerName || csn)
    ? `${partnerName || 'Partner'}${csn ? ` · CSN ${csn}` : ''} · ${partnerTier}`
    : null;

  const handleFieldEdit = (field) => {
    setServerErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const nextErrors = { ...previous };
      delete nextErrors[field];
      return nextErrors;
    });
    setSubmitError('');
  };

  const handleSubmitForChange = async (submitForPartner) => {
    if (submitForPartner === submitOnBehalfEnabled) {
      return;
    }

    if (hasUnsavedChanges) {
      const shouldSwitch = await Swal.fire({
        icon: 'warning',
        title: 'Unsaved changes',
        text: 'Switching submit mode keeps your typed project details, but partner selection may change. Continue?',
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#052140',
        cancelButtonColor: '#6b7280',
        reverseButtons: true,
        customClass: {
          title: 'swal-title-custom',
          text: 'swal-text-custom',
        },
      });
      if (!shouldSwitch.isConfirmed) {
        return;
      }
    }

    setSubmitOnBehalfEnabled(submitForPartner);
    const nextOnBehalf = EMPTY_PARTNER_ON_BEHALF_SELECTION;
    setOnBehalfSelection(nextOnBehalf);
    setOnBehalfErrors({});
    setSubmitError('');
    setServerErrors({});
    if (formValuesRef.current) {
      persistDraft(formValuesRef.current, submitForPartner, nextOnBehalf);
    }
  };

  const handleSubmitError = async (error) => {
    if (error instanceof PartnerApiError && Object.keys(error.fieldErrors).length > 0) {
      const { formErrors, onBehalfErrors: nextOnBehalfErrors } = splitSubmitFieldErrors(
        error.fieldErrors,
        { onBehalfMode: showOnBehalfSelection },
      );

      setServerErrors(formErrors);
      if (Object.keys(nextOnBehalfErrors).length > 0) {
        setOnBehalfErrors(nextOnBehalfErrors);
      }

      const combinedErrors = { ...nextOnBehalfErrors, ...formErrors };
      setSubmitError(
        getFirstFieldErrorMessage(combinedErrors) || 'Please review the fields marked in red.',
      );
      scrollToFirstPartnerFormError();

      await showPartnerValidationErrorPopup({
        title: 'Unable to submit project',
        fieldErrors: combinedErrors,
      });
      return;
    }

    setSubmitError(getPartnerApiErrorMessage(error, 'Failed to submit project.'));
  };

  const clearDraftAfterSubmit = () => {
    allowNavigationRef.current = true;
    clearSubmitProjectDraft(draftUserKey);
    setHasUnsavedChanges(false);
  };

  const handleSubmit = async (payload) => {
    if (showOnBehalfSelection) {
      const onBehalfValidation = validatePartnerOnBehalfSelection(onBehalfSelection);
      if (!onBehalfValidation.isValid) {
        setOnBehalfErrors(onBehalfValidation.errors);
        setSubmitError(getFirstFieldErrorMessage(onBehalfValidation.errors)
          || 'Please complete the partner selection fields.');
        scrollToFirstPartnerFormError();
        return;
      }

      setSubmitting(true);
      setSubmitError('');
      setServerErrors({});
      setOnBehalfErrors({});

      try {
        await createProject({
          ...payload,
          companyName: onBehalfSelection.companyName,
          csn: onBehalfSelection.csn,
          partnerName: onBehalfSelection.partnerName.trim(),
          partnerEmail: onBehalfSelection.partnerEmail.trim(),
        });

        clearDraftAfterSubmit();

        await Swal.fire({
          icon: 'success',
          title: 'Project Submitted',
          text: `Project submitted on behalf of ${onBehalfSelection.companyName || 'the partner'}.`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#052140',
          customClass: {
            title: 'swal-title-custom',
            text: 'swal-text-custom',
          },
        });

        navigate(PARTNER_PORTAL_PAE_QUEUE);
      } catch (error) {
        await handleSubmitError(error);
      } finally {
        setSubmitting(false);
      }

      return;
    }

    if (!partnerEmail) {
      setSubmitError('Partner email was not found in your login session. Please sign in again or contact support.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setServerErrors({});

    try {
      await createProject({
        ...payload,
        partnerEmail,
      });
      clearDraftAfterSubmit();
      await showPartnerProjectSubmittedPopup();
      navigate(PARTNER_PORTAL_MY_PROJECTS);
    } catch (error) {
      await handleSubmitError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const pageDescription = showOnBehalfSelection
    ? 'Submit on behalf of a partner organization.'
    : 'Register a new, active, or completed delivery for pipeline and PAE review.';

  const showPartnerStyleForm = isPartnerSubmitMode || (isStaffSubmitMode && !submitOnBehalfEnabled);
  const showInitialLoading = !formReady && optionsLoading;

  return (
    <>
      <Navbar activeTab="PARTNER_PORTAL" />
      <PartnerLayout access={access}>
        <div className="partner-submit-page">
          <PageHeader
            eyebrow="Delivery Services Hub"
            title="Submit project"
            description={pageDescription}
            rightContent={
              (isStaffSubmitMode || accountBadgeText)
                ? (
                  <>
                    {isStaffSubmitMode ? (
                      <span className="psm-queue-view-pill">PAE view</span>
                    ) : null}
                    {accountBadgeText ? (
                      <span className="partner-account-badge">{accountBadgeText}</span>
                    ) : null}
                  </>
                )
                : null
            }
          />

          {showInitialLoading ? (
            <div className="partner-submit-loading">
              <span className="partner-submit-loading-spinner" aria-hidden="true" />
              <p>Loading form options…</p>
            </div>
          ) : (
            <div className="partner-submit-body">
              {hasUnsavedChanges ? (
                <p className="partner-submit-draft-banner" role="status">
                  Draft saved on this device — your typed details stay until you submit or leave.
                </p>
              ) : null}

              {isStaffSubmitMode ? (
                <div className="partner-submit-mode-panel">
                  <p className="partner-submit-mode-label">Submitting for</p>
                  <div className="partner-segmented-control partner-segmented-control--modern" role="group" aria-label="Submitting for">
                    <button
                      type="button"
                      className={`partner-segmented-option ${!submitOnBehalfEnabled ? 'is-selected' : ''}`}
                      aria-pressed={!submitOnBehalfEnabled}
                      onClick={() => handleSubmitForChange(false)}
                    >
                      My organization
                    </button>
                    <button
                      type="button"
                      className={`partner-segmented-option ${submitOnBehalfEnabled ? 'is-selected' : ''}`}
                      aria-pressed={submitOnBehalfEnabled}
                      onClick={() => handleSubmitForChange(true)}
                    >
                      Partner organization
                    </button>
                  </div>
                </div>
              ) : null}

              {showOnBehalfSelection ? (
                <PartnerOnBehalfSelection
                  companies={companies}
                  loading={companiesLoading}
                  error={companiesError}
                  selection={onBehalfSelection}
                  fieldErrors={onBehalfErrors}
                  onChange={handleOnBehalfChange}
                />
              ) : null}

              <ProjectForm
                prefill={showPartnerStyleForm ? { partnerName, csn } : {}}
                draftForm={savedDraft?.form || null}
                formOptions={formOptions}
                onSubmit={handleSubmit}
                onFieldEdit={handleFieldEdit}
                onFormChange={handleFormChange}
                submitting={submitting}
                submitError={submitError}
                serverErrors={serverErrors}
                hidePartnerFields={showOnBehalfSelection}
                submitButtonLabel={showOnBehalfSelection ? 'Submit on behalf' : 'Submit project'}
              />
            </div>
          )}
        </div>
      </PartnerLayout>
    </>
  );
};

export default SubmitProjectPage;
